import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, MapPin, Loader2, AlertTriangle, CheckCircle2, Timer, History, Download, Smartphone, FileText, PenLine, Upload, FolderOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export default function PontoFuncionarioPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [geoStatus, setGeoStatus] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // PWA install
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const deferredPrompt = useRef(null);

  // Documents
  const [docs, setDocs] = useState([]);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [signing, setSigning] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const uploadFileRef = useRef();

  // Live clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; setInstallPrompt(true); };
    window.addEventListener("beforeinstallprompt", handler);
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;
    if (isIos) setShowIosHint(true);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      if (outcome === "accepted") { setInstallPrompt(false); toast.success("App instalado!"); }
      deferredPrompt.current = null;
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [infoRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/v1/timeclock/token-info/${token}`),
        axios.get(`${API}/api/v1/timeclock/token-status/${token}`),
      ]);
      setInfo(infoRes.data);
      setStatus(statusRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Token inválido ou expirado");
    } finally { setLoading(false); }
  }, [token]);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/v1/employee-docs/my/${token}`);
      setDocs(res.data);
    } catch { }
  }, [token]);

  useEffect(() => { fetchData(); fetchDocs(); }, [fetchData, fetchDocs]);

  // Canvas drawing helpers
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const endDraw = (e) => { e?.preventDefault(); isDrawing.current = false; };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    if (!signedName.trim()) { toast.error("Escreva o seu nome completo"); return; }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blank = document.createElement("canvas");
    blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { toast.error("Desenhe a sua assinatura"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/api/v1/employee-docs/${signingDoc.id}/sign`, {
        token,
        signature_data: canvas.toDataURL("image/png"),
        signed_name: signedName.trim(),
      });
      toast.success("Documento assinado!");
      setSigningDoc(null);
      setSignedName("");
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao assinar");
    }
    setSigning(false);
  };

  const handleEmployeeUpload = async () => {
    if (!uploadTitle.trim() || !uploadFile) { toast.error("Preencha o título e selecione um ficheiro"); return; }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          await axios.post(`${API}/api/v1/employee-docs/my/${token}/upload`, {
            title: uploadTitle.trim(),
            file_b64: ev.target.result,
            filename: uploadFile.name,
            content_type: uploadFile.type,
          });
          toast.success("Documento enviado!");
          setShowUploadDoc(false);
          setUploadTitle("");
          setUploadFile(null);
          fetchDocs();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erro ao enviar");
        }
        setUploading(false);
      };
      reader.readAsDataURL(uploadFile);
    } catch { setUploading(false); }
  };

  const getLocation = () => {
    return new Promise((resolve) => {
      setGeoStatus("loading");
      if (!navigator.geolocation) { setGeoStatus("error"); resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => { setGeoStatus("success"); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => { setGeoStatus("error"); resolve(null); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleClockIn = async () => {
    setProcessing(true);
    const loc = await getLocation();
    if (!loc && info?.has_geolocation) {
      toast.error("GPS obrigatório. Você precisa estar no local de trabalho para fazer check-in. Ative a localização no seu dispositivo.");
      setProcessing(false);
      return;
    }
    try {
      await axios.post(`${API}/api/v1/timeclock/token-clock-in/${token}`, { lat: loc?.lat, lng: loc?.lng });
      toast.success("Entrada registrada!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar entrada");
    }
    setProcessing(false);
  };

  const handleClockOut = async () => {
    setProcessing(true);
    const loc = await getLocation();
    if (!loc && info?.has_geolocation) {
      toast.error("GPS obrigatório. Você precisa estar no local de trabalho para registrar saída. Ative a localização no seu dispositivo.");
      setProcessing(false);
      return;
    }
    try {
      const res = await axios.post(`${API}/api/v1/timeclock/token-clock-out/${token}`, { lat: loc?.lat, lng: loc?.lng });
      toast.success(`Saída registrada! ${res.data.hours_worked}h trabalhadas`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar saída");
    }
    setProcessing(false);
  };

  const getElapsedTime = (clockIn) => {
    const diff = (currentTime - new Date(clockIn)) / 1000;
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = Math.floor(diff % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-700 mb-2">Token Inválido</h2>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-sm text-muted-foreground mt-4">Contacte o seu gerente para obter um novo link de acesso.</p>
        </CardContent>
      </Card>
    </div>
  );

  const isClockedIn = status?.clocked_in;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white" data-testid="ponto-funcionario-page">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-4 py-5 text-center">
        {info?.logo_url && <img src={info.logo_url} alt="" className="w-12 h-12 rounded-full mx-auto mb-2 object-cover bg-white" />}
        <h1 className="text-lg font-bold">{info?.restaurant_name}</h1>
        <p className="text-emerald-100 text-xs">{info?.restaurant_address}</p>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        {/* Employee Info */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl font-black text-emerald-700">{info?.member_name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <h2 className="text-xl font-bold" data-testid="employee-name">{info?.member_name}</h2>
          <p className="text-sm text-muted-foreground">Ponto Eletrônico</p>
        </div>

        {/* Current Time */}
        <div className="text-center">
          <p className="text-4xl font-black tracking-tight tabular-nums" data-testid="current-time">
            {currentTime.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </p>
          <p className="text-sm text-muted-foreground">
            {currentTime.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>

        {/* Status & Action */}
        {isClockedIn ? (
          <Card className="border-2 border-emerald-300 bg-emerald-50" data-testid="clocked-in-card">
            <CardContent className="p-6 text-center space-y-4">
              <Badge className="bg-emerald-100 text-emerald-800 text-sm px-4 py-1">
                <Timer className="w-4 h-4 mr-1" />Em serviço
              </Badge>
              <div>
                <p className="text-xs text-muted-foreground">Entrada às</p>
                <p className="font-bold">{new Date(status.current_record.clock_in).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              <div className="text-3xl font-black tabular-nums text-emerald-700" data-testid="elapsed-timer">
                {getElapsedTime(status.current_record.clock_in)}
              </div>
              <Button
                onClick={handleClockOut}
                disabled={processing}
                className="w-full h-14 text-lg gap-2 bg-red-600 hover:bg-red-700"
                data-testid="clock-out-btn"
              >
                {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
                Registrar Saída
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-gray-200" data-testid="clocked-out-card">
            <CardContent className="p-6 text-center space-y-4">
              <Badge variant="outline" className="text-sm px-4 py-1">Fora de serviço</Badge>
              <Button
                onClick={handleClockIn}
                disabled={processing}
                className="w-full h-14 text-lg gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="clock-in-btn"
              >
                {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
                Registrar Entrada
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Geolocation Status */}
        {geoStatus && (
          <div className={`flex items-center justify-center gap-2 text-xs rounded-lg px-3 py-2 ${
            geoStatus === "success" ? "bg-emerald-50 text-emerald-700" :
            geoStatus === "error" && info?.has_geolocation ? "bg-red-50 text-red-700" :
            "bg-muted/50 text-muted-foreground"
          }`}>
            {geoStatus === "loading" && <><Loader2 className="w-3 h-3 animate-spin" /><span>Verificando localização...</span></>}
            {geoStatus === "success" && <><MapPin className="w-3 h-3" /><span>Localização verificada — você está no local</span></>}
            {geoStatus === "error" && info?.has_geolocation && <><AlertTriangle className="w-3 h-3" /><span>GPS bloqueado. Ative a localização para registrar ponto.</span></>}
            {geoStatus === "error" && !info?.has_geolocation && <><AlertTriangle className="w-3 h-3 text-amber-500" /><span className="text-amber-600">GPS indisponível</span></>}
          </div>
        )}

        {/* Geolocation info */}
        {info?.has_geolocation && !geoStatus && (
          <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
            <MapPin className="w-3 h-3" /> Ponto validado por geolocalização (raio de 50m)
          </p>
        )}

        {/* Summary totals */}
        {status?.recent_records?.length > 0 && (() => {
          const closed = status.recent_records.filter(r => r.status === "closed");
          const totalH = closed.reduce((s, r) => s + (r.hours_worked || 0), 0);
          const totalOT = closed.reduce((s, r) => s + (r.overtime_hours || 0), 0);
          return (
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-black text-emerald-700">{totalH.toFixed(1)}h</p>
                  <p className="text-[11px] text-muted-foreground">Total trabalhado</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-black text-amber-700">{totalOT.toFixed(1)}h</p>
                  <p className="text-[11px] text-muted-foreground">Horas extra</p>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* All Records (open + closed) */}
        {status?.recent_records?.length > 0 && (
          <div>
            <h3 className="text-xs font-bold uppercase text-muted-foreground mb-2 flex items-center gap-1">
              <History className="w-3 h-3" />Histórico ({status.recent_records.length} registos)
            </h3>
            <div className="space-y-2">
              {status.recent_records.map(r => {
                const isOpen = r.status === "open" || !r.clock_out;
                return (
                  <Card key={r.id} className={`border ${isOpen ? "border-amber-300 bg-amber-50" : "border-border/50"}`}>
                    <CardContent className="p-3 flex justify-between items-center text-sm">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium">
                            {new Date(r.clock_in).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                          </p>
                          {isOpen && <span className="text-[9px] bg-amber-400 text-white font-bold px-1.5 py-0.5 rounded-full">EM ABERTO</span>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(r.clock_in).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                          {" → "}
                          {r.clock_out
                            ? new Date(r.clock_out).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
                            : <span className="text-amber-600 font-medium">sem saída</span>
                          }
                        </p>
                      </div>
                      <div className="text-right">
                        {r.hours_worked > 0
                          ? <p className="font-bold text-emerald-700">{r.hours_worked.toFixed(1)}h</p>
                          : <p className="text-xs text-muted-foreground">—</p>
                        }
                        {r.overtime_hours > 0 && <p className="text-[10px] text-amber-600">+{r.overtime_hours.toFixed(1)}h extra</p>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* No records yet */}
        {(!status?.recent_records || status.recent_records.length === 0) && (
          <p className="text-center text-sm text-muted-foreground py-4">Sem registros ainda. Clique em "Registrar Entrada" para começar.</p>
        )}

        {/* Documents section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1">
              <FolderOpen className="w-3 h-3" />Documentos ({docs.length})
            </h3>
            <Button size="sm" variant="outline" className="gap-1 text-xs h-7" onClick={() => setShowUploadDoc(true)}>
              <Upload className="w-3 h-3" />Enviar doc
            </Button>
          </div>
          {docs.length === 0 ? (
            <p className="text-xs text-center text-muted-foreground py-3">Nenhum documento</p>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => {
                const isPending = doc.status === "pending" && doc.requires_signature;
                return (
                  <Card key={doc.id} className={`border ${isPending ? "border-amber-300 bg-amber-50" : doc.status === "signed" ? "border-emerald-200 bg-emerald-50" : "border-border"}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <FileText className={`w-8 h-8 shrink-0 ${isPending ? "text-amber-500" : doc.status === "signed" ? "text-emerald-600" : "text-blue-500"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{doc.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.month ? `Mês: ${doc.month} · ` : ""}
                          {new Date(doc.uploaded_at).toLocaleDateString("pt-BR")}
                        </p>
                        {doc.status === "signed" && (
                          <p className="text-xs text-emerald-700">✅ Assinado em {new Date(doc.signed_at).toLocaleDateString("pt-BR")}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {isPending && (
                          <Button size="sm" onClick={() => { setSigningDoc(doc); setSignedName(""); clearCanvas(); }}
                            className="gap-1 h-8 bg-amber-500 hover:bg-amber-600 text-white text-xs">
                            <PenLine className="w-3 h-3" />Assinar
                          </Button>
                        )}
                        {doc.file_url && (
                          <a href={doc.file_url} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" className="gap-1 h-8 text-xs w-full">
                              <Download className="w-3 h-3" />Ver
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Sign document modal */}
        <Dialog open={!!signingDoc} onOpenChange={() => setSigningDoc(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-sm font-bold uppercase">Assinar Documento</DialogTitle></DialogHeader>
            {signingDoc && (
              <div className="space-y-4">
                <div className="bg-muted rounded-lg p-3">
                  <p className="font-semibold text-sm">{signingDoc.title}</p>
                  {signingDoc.month && <p className="text-xs text-muted-foreground">Mês: {signingDoc.month}</p>}
                </div>
                <div>
                  <Label className="text-xs">Nome completo *</Label>
                  <Input value={signedName} onChange={e => setSignedName(e.target.value)} placeholder="Escreva o seu nome" className="mt-1" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Assinatura (desenhe com o dedo) *</Label>
                    <button onClick={clearCanvas} className="text-xs text-muted-foreground underline">Limpar</button>
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={280} height={120}
                    className="border-2 border-dashed border-border rounded-lg bg-white w-full touch-none"
                    style={{ touchAction: "none" }}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Ao assinar confirma que recebeu e leu este documento.</p>
                </div>
                <Button onClick={handleSign} disabled={signing} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2">
                  {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                  {signing ? "A assinar..." : "Confirmar Assinatura"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Employee upload modal */}
        <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-sm font-bold uppercase">Enviar Documento</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Envie documentos pessoais para a empresa (NIF, IBAN, certificados, etc.)</p>
              <div>
                <Label className="text-xs">Título *</Label>
                <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Ex: NIF, Comprovativo IBAN..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Ficheiro (PDF, imagem — máx 10MB)</Label>
                <div onClick={() => uploadFileRef.current?.click()}
                  className="mt-1 border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                  {uploadFile ? (
                    <p className="text-sm font-medium text-blue-700">{uploadFile.name}</p>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                      <p className="text-xs text-muted-foreground">Toque para selecionar</p>
                    </>
                  )}
                </div>
                <input ref={uploadFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f && f.size <= 10*1024*1024) setUploadFile(f); else if (f) toast.error("Máx 10MB"); }} />
              </div>
              <Button onClick={handleEmployeeUpload} disabled={uploading} className="w-full gap-2 bg-blue-600 hover:bg-blue-700">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "A enviar..." : "Enviar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Install App */}
        {installPrompt && (
          <Card className="border-2 border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 flex items-center gap-3">
              <Smartphone className="w-8 h-8 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-emerald-800">Instalar como App</p>
                <p className="text-xs text-emerald-700">Adicione à tela inicial para acesso rápido</p>
              </div>
              <Button size="sm" onClick={handleInstall} className="bg-emerald-600 hover:bg-emerald-700 gap-1 shrink-0">
                <Download className="w-3.5 h-3.5" />Instalar
              </Button>
            </CardContent>
          </Card>
        )}
        {showIosHint && !installPrompt && (
          <Card className="border border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center space-y-1">
              <Smartphone className="w-6 h-6 text-blue-600 mx-auto" />
              <p className="text-xs font-bold text-blue-800">Adicionar à tela inicial</p>
              <p className="text-xs text-blue-700">Toque em <strong>Compartilhar</strong> e depois <strong>"Adicionar à tela de início"</strong> para instalar como app</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-[10px] text-center text-muted-foreground pt-4">
          Powered by Kaiso System
        </p>
      </div>
    </div>
  );
}
